import { Link } from "@remix-run/react";
import { ArrowRight } from "lucide-react";
import {
  getRegionAwareSearchUrl,
  getWorkspaceSearchUrl,
} from "~/lib/helpers/ipLocationSearchPathHelper";
import { getRegionUrlPrefixWithCountryCode } from "~/lib/helpers/slugHelper";
import { useRegionConfig } from "~/store/regionConfigStore";
import { Card } from "~/components/ui/card";
import {
  NAV_DROPDOWN_CARD_CLASS,
  NAV_DROPDOWN_MAIN_PANEL_CLASS,
  NAV_DROPDOWN_PROMO_PANEL_CLASS,
} from "../constant";
import {
  WORKSPACE_TYPE_ICONS,
  type WorkspaceTypeIconKey,
} from "./workspaceTypeIcons";

type WorkspaceNavItem = {
  title: string;
  description: string;
  workspaceType: WorkspaceTypeIconKey;
};

const workspaceColumnOne: WorkspaceNavItem[] = [
  {
    title: "Serviced Office",
    description:
      "Elevate your workspace experience with our fully serviced offices.",
    workspaceType: "serviced",
  },
  {
    title: "Private Office",
    description: "A private office setup with a desk, chair, and computer.",
    workspaceType: "private",
  },
  {
    title: "Enterprise Office",
    description:
      "Rent furnished workspaces equipped with the latest technology.",
    workspaceType: "enterprise",
  },
  {
    title: "Traditional Office",
    description: "A traditional office setup with a desk, chair, and computer.",
    workspaceType: "conventional",
  },
];

const workspaceColumnTwo: WorkspaceNavItem[] = [
  {
    title: "Coworking Office",
    description: "Collaborate with top professionals in shared, social spaces.",
    workspaceType: "coworking",
  },
  {
    title: "Shared Office",
    description:
      "Enjoy a lively work environment that promotes shared learning.",
    workspaceType: "shared",
  },
  {
    title: "Sublease Space",
    description:
      "Get a flexible, short-term workspace solution that suits you.",
    workspaceType: "sublet",
  },
  {
    title: "Virtual Office",
    description: "Build your professional presence with a prestigious address.",
    workspaceType: "virtual",
  },
];

type WorkspacesProps = {
  /** Region-aware search path from Header (coords omitted when IP country matches landing region) */
  searchUrl?: string;
};

export default function Workspaces({ searchUrl }: WorkspacesProps = {}) {
  const { regionConfig } = useRegionConfig();

  const regionPrefix = getRegionUrlPrefixWithCountryCode(regionConfig.region);
  const baseSearchUrl =
    searchUrl ?? getRegionAwareSearchUrl(regionConfig.region);

  const renderWorkspaceItem = (option: WorkspaceNavItem) => {
    const Icon = WORKSPACE_TYPE_ICONS[option.workspaceType];

    return (
      <Link
        data-spinner="true"
        key={option.title}
        to={getWorkspaceSearchUrl(baseSearchUrl, option.workspaceType)}
        className="group flex items-start gap-3 rounded-md p-2 transition-colors hover:bg-gray-100"
        prefetch="intent"
      >
        <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gray-200 text-oh-black transition-colors group-hover:bg-gray-200">
          <Icon aria-hidden />
        </div>

        <div className="min-w-0 space-y-0.5">
          <span className="text-base font-semibold leading-6 text-black transition-colors">
            {option.title}
          </span>

          <p className="line-clamp-2 text-sm leading-6 text-oh-gray">
            {option.description}
          </p>
        </div>
      </Link>
    );
  };

  return (
    <Card className={NAV_DROPDOWN_CARD_CLASS}>
      <div className={`${NAV_DROPDOWN_MAIN_PANEL_CLASS} p-6 lg:p-8`}>
        <span className='text-xs font-semibold uppercase tracking-[0.08em] text-oh-grey-heavy'>
          Workspace Type
        </span>

        <div className='mt-4 flex flex-col gap-1 lg:flex-row lg:gap-8'>
          <div className='flex min-w-0 w-full shrink-0 flex-col gap-1 lg:w-[300px]'>
            {workspaceColumnOne.map(renderWorkspaceItem)}
          </div>
          <div className='flex min-w-0 w-full shrink-0 flex-col gap-1 lg:w-[300px]'>
            {workspaceColumnTwo.map(renderWorkspaceItem)}
          </div>
        </div>
      </div>

      <div className={NAV_DROPDOWN_PROMO_PANEL_CLASS}>
        <span className='text-xs font-semibold uppercase tracking-[0.08em] text-oh-primary'>
          Find Space
        </span>

        <span className='mt-2 text-lg font-bold leading-7 text-black'>
          Workspaces for Every Business
        </span>

        <img
          src={`https://ik.imagekit.io/officehub/tr:h-600,f-avif/${process.env.PUBLIC_REMIX_APP_ASSESTS_IMAGES}/assets/home-page/space_type_header.avif`}
          alt='Modern flexible office space'
          className='mt-4 h-44 w-full rounded-lg object-cover'
          loading='lazy'
        />

        <p className='mt-4 text-sm leading-7 text-oh-dark-gray'>
          Whether you’re a{" "}
          <span className='font-semibold'>
            freelancer, startup, growing team, or enterprise,
          </span>{" "}
          find a workspace that fits the way you work.
        </p>

        <Link
          to={`${regionPrefix}/office-space`}
          className='mt-auto flex h-12 w-full shrink-0 items-center justify-center gap-2 rounded-md bg-oh-primary text-sm font-semibold text-white transition-colors hover:bg-oh-dullTeal'
          prefetch='intent'
        >
          Learn more
          <ArrowRight size={18} />
        </Link>
      </div>
    </Card>
  );
}
